import React, { createContext, useState, useEffect, useCallback, useRef, useContext } from 'react';
import { AuthContext } from './authContext';
import SessionWarningModal from '../components/SessionWarningModal';

// Decodifica el payload del JWT sin verificar firma (solo para leer exp en el cliente)
const getTokenRemainingMs = (token) => {
    try {
        if (!token) return null;
        const payloadBase64 = token.split('.')[1];
        const payload = JSON.parse(atob(payloadBase64));
        if (!payload.exp) return null;
        const remainingMs = payload.exp * 1000 - Date.now();
        return remainingMs > 0 ? remainingMs : 0;
    } catch {
        return null;
    }
};

export const SessionContext = createContext();

export const SessionProvider = ({ children }) => {
    const auth = useContext(AuthContext);
    const { user, token, role } = auth || {};

    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [showWarning, setShowWarning] = useState(false);
    const [timeLeft, setTimeLeft] = useState(0);
    const timerRef = useRef(null);
    const warningTimerRef = useRef(null);
    const isRefreshingRef = useRef(false);
    const pendingRequestsRef = useRef([]);
    // El usuario pidió logout: mientras esté en true, cualquier refresh (en vuelo o
    // en cola) se descarta. Un refresh que responde OK DESPUÉS del logout llamaría a
    // auth.login() y repoblaría el storage, resucitando la sesión con el mismo
    // usuario: es la carrera entre el POST /refresh y el UPDATE revoked del logout.
    const loggedOutRef = useRef(false);

    // Función base para fetch con manejo de credenciales
    const fetchWithCredentials = async (url, options = {}) => {
        const response = await fetch(url, {
            ...options,
            credentials: 'include', // Importante para enviar cookies
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
        });
        return response;
    };

    // Logout
    const logout = useCallback(async () => {
        // Cortar cualquier refresh en vuelo o encolado: un refresh que responda
        // después de acá se descarta (ver loggedOutRef en refreshAccessToken).
        loggedOutRef.current = true;
        pendingRequestsRef.current.forEach(({ reject }) => reject(false));
        pendingRequestsRef.current = [];

        try {
            await fetchWithCredentials('/api/authentication/logout', {
                method: 'POST',
            });
        } catch (error) {
            console.error('Error en logout:', error);
        } finally {
            auth?.logout();
            
            setIsAuthenticated(false);
            setShowWarning(false);
            setTimeLeft(0);
            if (timerRef.current) clearInterval(timerRef.current);
            if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
        }
    }, [auth]);

    // Función para refrescar el token
    const refreshAccessToken = useCallback(async () => {
        if (isRefreshingRef.current) {
            return new Promise((resolve, reject) => {
                pendingRequestsRef.current.push({ resolve, reject });
            });
        }

        isRefreshingRef.current = true;

        try {
            const response = await fetchWithCredentials('/api/authentication/refresh', {
                method: 'POST',
            });

            // Si el usuario cerró sesión mientras este refresh estaba en vuelo, la
            // respuesta ya no sirve: devolverla llamaría a auth.login() y la sesión
            // resucitaría en el mismo usuario. Se descarta y se falla.
            if (loggedOutRef.current) {
                pendingRequestsRef.current.forEach(({ reject }) => reject(false));
                pendingRequestsRef.current = [];
                return false;
            }

            if (response.ok) {
                const data = await response.json();

                auth?.login(data.username, data.jwtToken, data.role, data.userId, data.roles);

                pendingRequestsRef.current.forEach(({ resolve }) => resolve(data.jwtToken));
                pendingRequestsRef.current = [];

                return data.jwtToken;
            } else {
                pendingRequestsRef.current.forEach(({ reject }) => reject(false));
                pendingRequestsRef.current = [];
                return false;
            }
        } catch (error) {
            pendingRequestsRef.current.forEach(({ reject }) => reject(false));
            pendingRequestsRef.current = [];
            return false;
        } finally {
            isRefreshingRef.current = false;
        }
    }, [auth]);

    // Función para iniciar el contador de sesión
    const startSessionTimer = useCallback((expirationTime = 120000) => {
        if (timerRef.current) clearInterval(timerRef.current);
        if (warningTimerRef.current) clearTimeout(warningTimerRef.current);

        // Mostrar warning cuando falten 30 segundos
        warningTimerRef.current = setTimeout(() => {
            setShowWarning(true);
            setTimeLeft(30);

            timerRef.current = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 1) {
                        clearInterval(timerRef.current);
                        logout(); // Forzar logout al llegar a 0
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

        }, expirationTime - 30000);
    }, [logout]);

    // Extender sesión
    const extendSession = useCallback(async () => {
        const newToken = await refreshAccessToken();

        if (newToken) {
            setShowWarning(false);
            setTimeLeft(0);
            if (timerRef.current) clearInterval(timerRef.current);
            if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
            // Releer el exp del nuevo token tras el refresh
            const remainingMs = getTokenRemainingMs(newToken);
            startSessionTimer(remainingMs ?? 3600000);
        }

        return !!newToken;
    }, [refreshAccessToken, startSessionTimer]);

    // Login con fetch
    const login = useCallback(async (credentials) => {
        // Login explícito del usuario: habilita de nuevo el refresh (un logout previo
        // lo había deshabilitado mientras durara la sesión).
        loggedOutRef.current = false;

        try {
            const response = await fetchWithCredentials(`/api/authentication/login`, {
                method: "POST",
                body: JSON.stringify(credentials),
            });

            if (response.ok) {
                const responseData = await response.json();
                console.log("ResponseData login:: ", responseData);

                auth?.login(responseData.username, responseData.jwtToken, responseData.role, responseData.userId, responseData.roles);

                return { success: true, data: responseData };
            } else {
                const errorData = await response.json().catch(() => ({}));
                return { success: false, error: errorData.message || 'Credenciales inválidas' };
            }
        } catch (error) {
            console.error('Error en login:', error);
            return { success: false, error: 'Error de conexión' };
        }
    }, [auth]);

    // Función para hacer peticiones autenticadas
    const authenticatedFetch = useCallback(async (url, options = {}) => {
        const makeRequest = async () => {
            const response = await fetchWithCredentials(url, options);

            if (response.status === 401) {
                const refreshed = await refreshAccessToken();
                if (refreshed) {
                    return await fetchWithCredentials(url, options);
                } else {
                    await logout();
                    throw new Error('Sesión expirada');
                }
            }

            return response;
        };

        return makeRequest();
    }, [refreshAccessToken, logout]);

    useEffect(() => {
        if (auth?.isAuthenticated) {
            const remainingMs = getTokenRemainingMs(token);
            const timerMs = remainingMs ?? 3600000;
            console.log(`[SessionProvider] Token expira en ${Math.round(timerMs / 1000)}s`);
            startSessionTimer(timerMs);
        } else {
            if (timerRef.current) clearInterval(timerRef.current);
            if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
            setShowWarning(false);
            setTimeLeft(0);
        }

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
        };
    }, [auth?.isAuthenticated, startSessionTimer, token]);

    // Escuchar el evento de visibilidad para controlar expiración en background
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && auth?.isAuthenticated && token) {
                const remainingMs = getTokenRemainingMs(token);
                if (remainingMs !== null && remainingMs <= 0) {
                    console.log("[SessionProvider] Token expiró mientras la pestaña estaba oculta. Ejecutando logout.");
                    logout();
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [auth?.isAuthenticated, token, logout]);

    const value = {
        isAuthenticated,
        user,
        showWarning,
        timeLeft,
        login,
        logout,
        extendSession,
        authenticatedFetch
    };

    return (
        <SessionContext.Provider value={value}>
            {children}
            {showWarning && (
                <SessionWarningModal
                    timeLeft={timeLeft}
                    onContinue={extendSession}
                    onLogout={logout}
                />
            )}
        </SessionContext.Provider>
    );
};
